import { useCallback, useEffect, useRef, useState } from "react";
import {
  normalizeStationCameraTarget,
  resolveStationCameraKey,
  setStationCameraTarget,
} from "../utils/stationCameraBinding";

const PRESENCE_TTL_MS = 45000;
const PRESENCE_SWEEP_MS = 10000;

/**
 * Camera phone: announce this device as the station (subgroup) camera.
 * Coach: collect announcements so chrono / group record can auto-select them.
 */
export function useStationCameraPresence({
  socket = null,
  tsId = null,
  enabled = false,
  tsgId = null,
  userId = null,
  playerId = null,
  label = null,
  /** When true, listen for presence (coach). */
  listen = false,
} = {}) {
  const [byTsgId, setByTsgId] = useState(() => ({}));
  const lastSeenRef = useRef(new Map());

  const announce = useCallback(() => {
    if (!socket || !tsId || !enabled || !tsgId || !userId) return;
    socket.emit("announceStationCamera", {
      ts_id: String(tsId),
      tsg_id: String(tsgId),
      user_id: String(userId),
      player_id: playerId != null ? String(playerId) : null,
      label: label || null,
      online: true,
    });
    const key = resolveStationCameraKey({ tsgId });
    setStationCameraTarget(key, {
      kind: "station",
      tsgId: String(tsgId),
      userId: String(userId),
      playerId: playerId != null ? String(playerId) : null,
      label: label || null,
    });
  }, [socket, tsId, enabled, tsgId, userId, playerId, label]);

  const clearAnnounce = useCallback(() => {
    if (!socket || !tsId || !tsgId || !userId) return;
    socket.emit("announceStationCamera", {
      ts_id: String(tsId),
      tsg_id: String(tsgId),
      user_id: String(userId),
      player_id: playerId != null ? String(playerId) : null,
      online: false,
    });
  }, [socket, tsId, tsgId, userId, playerId]);

  useEffect(() => {
    if (!socket || !listen) return undefined;
    const onPresence = (payload = {}) => {
      const tsg = payload.tsg_id != null ? String(payload.tsg_id) : null;
      const uid = payload.user_id != null ? String(payload.user_id) : null;
      if (!tsg) return;
      if (payload.online === false || !uid) {
        lastSeenRef.current.delete(tsg);
        setByTsgId((prev) => {
          if (!prev[tsg]) return prev;
          const next = { ...prev };
          delete next[tsg];
          return next;
        });
        return;
      }
      const target = normalizeStationCameraTarget({
        kind: "station",
        tsgId: tsg,
        userId: uid,
        playerId: payload.player_id != null ? String(payload.player_id) : null,
        label: payload.label || null,
      });
      if (!target) return;
      lastSeenRef.current.set(tsg, Date.now());
      setByTsgId((prev) => ({ ...prev, [tsg]: target }));
      const key = resolveStationCameraKey({ tsgId: tsg });
      setStationCameraTarget(key, target);
    };
    socket.on("stationCameraPresence", onPresence);
    const sweep = window.setInterval(() => {
      const now = Date.now();
      const lastSeen = lastSeenRef.current;
      const stale = [];
      for (const [tsg, at] of lastSeen.entries()) {
        if (now - at > PRESENCE_TTL_MS) stale.push(tsg);
      }
      if (!stale.length) return;
      for (const tsg of stale) lastSeen.delete(tsg);
      setByTsgId((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const tsg of stale) {
          if (next[tsg]) {
            delete next[tsg];
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, PRESENCE_SWEEP_MS);
    return () => {
      socket.off("stationCameraPresence", onPresence);
      window.clearInterval(sweep);
    };
  }, [socket, listen]);

  useEffect(() => {
    if (!enabled || !socket || !tsId || !tsgId || !userId) {
      return undefined;
    }
    announce();
    const id = window.setInterval(announce, 15000);
    return () => {
      window.clearInterval(id);
      clearAnnounce();
    };
  }, [enabled, socket, tsId, tsgId, userId, announce, clearAnnounce]);

  const cameraForTsg = useCallback(
    (id) => {
      if (id == null) return null;
      return byTsgId[String(id)] || null;
    },
    [byTsgId]
  );

  return {
    stationCamerasByTsgId: byTsgId,
    cameraForTsg,
    announce,
  };
}

export default useStationCameraPresence;
